import {
  faChartColumn,
  faCompass,
  faGear,
  faHeadset,
  faUsers,
  faGrid2,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faBars,
  faXmark,
  faArrowRightFromBracket,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import auth from '@client/components/auth';
import { TMap } from '../../helpers/types';
import { PATHS } from '../../../routes';
import { getRole, toTestId } from './helpers';
import { PermissionScope } from '@playbook-technologies/common-enums';
import { isEmpty } from 'lodash';
import { IndexLink } from '@client/router/compat';
import { Link } from '@client/router/compat';
import { PlaybookLogo } from '@design-system/components/logo/PlaybookLogo';
import { getVersion } from '../../../../releases';
import { clsx } from 'clsx';
import { useEffect, useState, type MouseEventHandler } from 'react';
import { browserHistory } from '@client/router/compat';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import * as styles from './DashboardMenu.css';

type MenuItem = {
  caption: string;
  to: string;
  icon: IconProp;
  externalLink?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  testId: string;
};

type Role = ReturnType<typeof getRole>;

const menuItem = (
  caption: string,
  to: string,
  icon: IconProp,
  options?: { externalLink?: boolean },
): MenuItem => ({
  caption,
  to,
  icon,
  externalLink: options?.externalLink,
  testId: toTestId(caption),
});

const openWithLoginBootstrap = ({
  bootstrapUrl,
  targetUrl,
}: {
  bootstrapUrl: string;
  targetUrl: string;
}): void => {
  const iframe = document.createElement('iframe');
  const loginEvent = { isLoginData: true, data: auth.getUser() };

  iframe.src = bootstrapUrl;
  iframe.width = '0px';
  iframe.height = '0px';
  iframe.setAttribute('aria-hidden', 'true');

  document.body.appendChild(iframe);

  iframe.onload = () => {
    window.setTimeout(() => {
      iframe.contentWindow?.postMessage(loginEvent, bootstrapUrl);

      window.setTimeout(() => {
        const openedWindow = window.open(targetUrl, '_blank');
        openedWindow?.focus();
        iframe.remove();
      }, 500);
    }, 500);
  };
};

const ssoMenuItem = (
  caption: string,
  bootstrapUrl: string,
  targetUrl: string,
  icon: IconProp,
): MenuItem => ({
  ...menuItem(caption, targetUrl, icon, { externalLink: true }),
  onClick: (event) => {
    event.preventDefault();
    openWithLoginBootstrap({ bootstrapUrl, targetUrl });
  },
});

const getMenuItems = (
  customPermissions: Array<PermissionScope>,
): MenuItem[] => {
  const role = getRole();

  const cmsUrl = import.meta.env.VITE_CMS_URL;
  const supportUrl = import.meta.env.VITE_SUPPORT_TOOL;

  const menuItemKeys = [
    'Creators',
    'Analytics',
    'Users',
    'Config',
    'Discovery',
    'Support',
  ] as const;
  type MenuItemKey = (typeof menuItemKeys)[number];

  const allItems: Record<MenuItemKey, MenuItem> = {
    Creators: menuItem('Creators', PATHS.HOME, faGrid2),
    Analytics: menuItem('Analytics', PATHS.ANALYTICS, faChartColumn),
    Users: menuItem('Users', PATHS.PORTAL_USERS, faUsers),
    Config: menuItem('Config', PATHS.CONFIG_FILE, faGear),
    Discovery: ssoMenuItem(
      'Discovery',
      cmsUrl,
      `${cmsUrl}/discovery`,
      faCompass,
    ),
    Support: ssoMenuItem('Support', supportUrl, supportUrl, faHeadset),
  };

  const roleItemsMap: TMap<Role, MenuItemKey[]> = {
    admin: ['Creators', 'Analytics', 'Users', 'Config'],
    moderator: ['Creators', 'Analytics'],
    contentEditor: ['Creators'],
    unknown: [],
  };

  const itemNames = [...roleItemsMap[role]];

  if (
    !isEmpty(customPermissions) &&
    customPermissions.includes(PermissionScope.InternalTools)
  ) {
    itemNames.push('Discovery', 'Support');
  }

  return itemNames.map((name) => allItems[name]);
};

const normalizePath = (path: string): string =>
  path.startsWith('/') ? path : `/${path}`;

const AdminMenuItem = (p: {
  item: MenuItem;
  pathname: string;
  onClick?: () => void;
}) => {
  const isActive =
    !p.item.externalLink &&
    normalizePath(p.item.to) === normalizePath(p.pathname);

  if (p.item.externalLink) {
    return (
      <a
        href={p.item.to}
        target="_blank"
        rel="noopener noreferrer"
        className={clsx(styles.menuItem, isActive && styles.menuItemActive)}
        data-testid={p.item.testId}
        onClick={(event) => {
          p.item.onClick?.(event);
          p.onClick?.();
        }}
      >
        <span className={styles.menuItemIcon}>
          <FontAwesomeIcon icon={p.item.icon} />
        </span>
        <span className={styles.menuItemLabel}>{p.item.caption}</span>
      </a>
    );
  }

  return (
    <IndexLink
      to={p.item.to}
      activeClassName={styles.menuItemActive}
      className={clsx(styles.menuItem, isActive && styles.menuItemActive)}
      data-testid={p.item.testId}
      onClick={p.onClick}
    >
      <span className={styles.menuItemIcon}>
        <FontAwesomeIcon icon={p.item.icon} />
      </span>
      <span className={styles.menuItemLabel}>{p.item.caption}</span>
    </IndexLink>
  );
};

export const DashboardMenu = ({
  logout,
  customPermissions,
}: {
  logout: () => void;
  customPermissions: Array<PermissionScope>;
}) => {
  const [open, setOpen] = useState(false);
  const [pathname, setPathname] = useState(window.location.pathname);
  const items = getMenuItems(customPermissions);
  useEffect(() => {
    const unlisten = browserHistory.listen((location) => {
      setPathname(location.pathname);
    });
    return () => unlisten();
  }, []);

  return (
    <nav className={styles.wrapper}>
      <div className={styles.header}>
        <Link to={PATHS.HOME} className={styles.desktopOnly}>
          <PlaybookLogo height={16} showGlyph color="W-100" />
        </Link>
        <button
          type="button"
          className={styles.hamburgerButton}
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? 'Close menu' : 'Open menu'}
        >
          <FontAwesomeIcon icon={open ? faXmark : faBars} width={18} />
        </button>
      </div>

      <div
        className={clsx(
          styles.mobileHideable,
          open && styles.mobileHideableOpen,
        )}
      >
        <div className={styles.menuList}>
          {items.map((item) => (
            <AdminMenuItem
              key={item.caption}
              item={item}
              pathname={pathname}
              onClick={() => setOpen(false)}
            />
          ))}
        </div>

        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.logoutButton}
            onClick={logout}
            aria-label="Log out"
          >
            <FontAwesomeIcon icon={faArrowRightFromBracket} />
          </button>
          <span className={styles.versionText}>v{getVersion()}</span>
        </footer>
      </div>
    </nav>
  );
};
